import { useEffect, useState } from "react";
import Picker, { PickerOption } from "../widgets/components/SimplePicker";
import { BranchDetails } from "../../types";
import { fetchBranches, requestDeleteBranchConfirmation, switchBranch } from "../../api";
import { AuthModel, useAuth } from "../auth";
import { getUserByToken } from "../auth/core/_requests";
import { useAppContext } from "../apps/AppContext";
import { toast } from "react-toastify";
import { canUseModule, Edition, getEffectiveEdition } from "../subscription/subscriptionAccess";

interface BranchPickerProp {
  label?: string
}
const defaultBranch =[ { branchId: 'MAIN', branchName: 'Main'},{ branchId: 'ALL', branchName: 'All'},]
export const BranchPicker: React.FC<BranchPickerProp> = ({ label }) => {
  const { saveAuth, setCurrentUser, switchToken } = useAuth()
  const [branches, setBranches] = useState<BranchDetails[]>([]);
  const [selectedBranch, setSelectedBranch] = useState<BranchDetails>()
  const { subscriptionData } = useAppContext();

  const effectiveEdition: Edition = subscriptionData
    ? getEffectiveEdition(subscriptionData.currentPlan.edition as Edition, subscriptionData.currentPlan.status, subscriptionData.currentPlan.isTrial)
    : 'Free';

  useEffect(() => {
    if (!canUseModule('MultiBranch', effectiveEdition)) return;
    fetchBranches().then(result => {
      if (result.length === 0) return;
      setBranches([...defaultBranch, ...result]);
    });
  }, [effectiveEdition]);

  if (branches.length === 0) return null;

  const branchOptions = branches.map((branch: BranchDetails) => ({
    value: branch.branchId,
    label: branch.branchName || "",
  }));

  const handleSelectionChange = async (selectionOption: PickerOption) => {
    const auth: AuthModel = {
      api_token: '',
      refreshToken: undefined
    }
    const switchBranchResponse = await switchBranch(selectionOption.value)
    auth.api_token = switchBranchResponse.token
    saveAuth(auth)
    const user = await getUserByToken(auth.api_token)
    await switchToken();
    setCurrentUser(user)
  };

  return (
    <Picker label={label} options={branchOptions} value={selectedBranch?.branchId || ''} onSelectionChange={handleSelectionChange} />
  )
}

interface BranchFilterProp {
  branchId?: string,
  label?: string,
  onBranchSelection:(branchId:string)=>void;
}

export const BranchFilter: React.FC<BranchFilterProp> = ({ branchId, label, onBranchSelection }) => {
  const [branches, setBranches] = useState<BranchDetails[]>([]);
  const [selectedBranch, setSelectedBranch] = useState<BranchDetails>()
  const { subscriptionData } = useAppContext();

  const effectiveEdition: Edition = subscriptionData
    ? getEffectiveEdition(subscriptionData.currentPlan.edition as Edition, subscriptionData.currentPlan.status, subscriptionData.currentPlan.isTrial)
    : 'Free';

  useEffect(() => {
    if (!canUseModule('MultiBranch', effectiveEdition)) return;
    fetchBranches().then(result => {
      if (result.length === 0) return;
      const all = [...defaultBranch, ...result];
      setBranches(all);
      setSelectedBranch(all.find(br => br.branchId === branchId));
    });
  }, [effectiveEdition]);

  if (branches.length === 0) return null;

  const branchOptions = branches.map((branch: BranchDetails) => ({
    value: branch.branchId,
    label: branch.branchName || "",
  }));

  const handleSelectionChange = async (selectionOption: PickerOption) => {
    const branch = branches.find(br => br.branchId === selectionOption.value)
    setSelectedBranch(branch)
    onBranchSelection(selectionOption.value)
  }

  return (
    <Picker label={label || 'Branch'.toTranslatedDisplay()} options={branchOptions} value={selectedBranch?.branchId || ''} onSelectionChange={handleSelectionChange} />
  )
}

export const DeleteBranchSection: React.FC = () => {
  const { bizMaster } = useAppContext();
  const branchId = bizMaster?.branchId;
  const branchName = branchId;
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState('');

  // Only render for actual branches (not MAIN or empty)
  if (!branchId || branchId === 'MAIN' || branchId === 'ALL') return null;

  const handleSendConfirmationEmail = async () => {
    setModalLoading(true);
    setModalError('');
    try {
      await requestDeleteBranchConfirmation();
      setEmailSent(true);
      setShowModal(false);
      toast.success('A confirmation email has been sent to your registered email address. The link expires in 24 hours.'.toTranslatedDisplay());
    } catch (error: any) {
      setModalError(error?.response?.data?.message || 'Failed to send confirmation email. Please try again.'.toTranslatedDisplay());
    } finally {
      setModalLoading(false);
    }
  };

  return (
    <div className="mt-8">
      <div className="card border border-danger">
        <div className="card-header bg-light-danger">
          <h5 className="card-title text-danger mb-0">
            <i className="bi bi-exclamation-triangle me-2 text-danger"></i>
            {'Danger Zone'.toTranslatedDisplay()}
          </h5>
        </div>
        <div className="card-body">
          <p className="text-gray-700 mb-1">
            <strong>{'Permanently delete this branch and all its data.'.toTranslatedDisplay()}</strong>
          </p>
          <p className="text-muted fs-7 mb-4">
            {'This will remove all transactions, invoices, ledger entries, and inventory records for this branch.'.toTranslatedDisplay()}
            {' '}{'The main business and other branches will not be affected. This cannot be undone.'.toTranslatedDisplay()}
          </p>

          {emailSent ? (
            <div className="alert alert-success mb-4">
              <i className="bi bi-envelope-check me-2"></i>
              {'A confirmation email has been sent to your registered email address. The link expires in 24 hours.'.toTranslatedDisplay()}
              <div className="mt-2">
                <button className="btn btn-sm btn-outline-success" onClick={() => setEmailSent(false)}>
                  {'Send Again'.toTranslatedDisplay()}
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              className="btn btn-outline-danger"
              onClick={() => setShowModal(true)}
              disabled={loading}
            >
              <i className="bi bi-trash me-2"></i>
              {'Delete Branch'.toTranslatedDisplay()}
            </button>
          )}
        </div>
      </div>

      {/* Confirmation Modal */}
      {showModal && (
        <div className="modal d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header border-0 pb-0">
                <h5 className="modal-title text-danger">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  {'Delete Branch'.toTranslatedDisplay()} — {branchName}?
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => { setShowModal(false); setModalError(''); }}
                  disabled={modalLoading}
                />
              </div>
              <div className="modal-body">
                <p className="text-gray-700">
                  {'You are about to permanently delete'.toTranslatedDisplay()} <strong>{branchName}</strong>. {'This will remove all transactions, invoices, ledger entries, and inventory records for this branch. The main business and other branches will not be affected.'.toTranslatedDisplay()}
                </p>
                <p className="text-gray-700 mb-0">
                  {'A confirmation email will be sent to your registered address. You must click the link in that email to complete the deletion.'.toTranslatedDisplay()}
                </p>
                {modalError && (
                  <div className="alert alert-danger mt-3 mb-0">
                    {modalError}
                  </div>
                )}
              </div>
              <div className="modal-footer border-0">
                <button
                  type="button"
                  className="btn btn-light"
                  onClick={() => { setShowModal(false); setModalError(''); }}
                  disabled={modalLoading}
                >
                  {'Cancel'.toTranslatedDisplay()}
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleSendConfirmationEmail}
                  disabled={modalLoading}
                >
                  {modalLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      {'Sending...'.toTranslatedDisplay()}
                    </>
                  ) : (
                    <>
                      <i className="bi bi-envelope me-2"></i>
                      {'Send Confirmation Email'.toTranslatedDisplay()}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

